import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import swal from 'sweetalert2';
import { ConfiguracionGeneral } from 'src/app/shared/model/emision/configuracionGeneral.model';
import { DenunciasService } from '../denuncias.service';

@Component({
  selector: 'app-denuncias-configuracion',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './denuncias-configuracion.component.html',
  styleUrls: ['./denuncias-configuracion.component.css']
})
export class DenunciasConfiguracionComponent implements OnInit {

  correos: ConfiguracionGeneral[] = [];
  formulario: ConfiguracionGeneral = this.crearFormulario();
  cargando = false;
  guardando = false;
  eliminandoId: number | null = null;

  // Paginación de "Copias configuradas" (client-side, 10 por página)
  tamPagina = 10;
  paginaActual = 1;

  ngOnInit(): void {
    this.cargarCorreos();
  }

  constructor(
    private denunciasService: DenunciasService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  volver(): void {
    this.router.navigate(['/DENUNCIAS/kanban']);
  }

  cargarCorreos(): void {
    this.cargando = true;
    this.syncView();

    this.denunciasService.consultarCorreosCopia().subscribe({
      next: (resp) => {
        this.correos = (resp?.list || []).slice().sort((a, b) => (a.conf_fol_se || 0) - (b.conf_fol_se || 0));
        this.paginaActual = 1;
        this.cargando = false;
        this.syncView();
        // Solo el personal de la empresa duena del canal administra sus copias.
        if (resp?.pCodRetorno && resp.pCodRetorno !== '0') {
          this.mostrarError(resp.pMensaje || 'No se pudieron consultar los correos.');
        }
      },
      error: () => {
        this.correos = [];
        this.cargando = false;
        this.syncView();
      }
    });
  }

  editar(item: ConfiguracionGeneral): void {
    const esc = (s: string) => (s || '')
      .replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
    const alias = esc(item.conf_fol_se_dsc || '');
    const correo = esc(item.valor1 || '');

    void swal.fire({
      title: 'Editar copia administrativa',
      html:
        '<div style="text-align:left;display:grid;gap:6px">' +
        '<label style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8c97a5">Alias</label>' +
        `<input id="swalAlias" class="swal2-input" style="margin:0;width:100%" maxlength="80" placeholder="Ej. Cumplimiento" value="${alias}">` +
        '<label style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#8c97a5;margin-top:10px">Correo electrónico</label>' +
        `<input id="swalCorreo" class="swal2-input" style="margin:0;width:100%" maxlength="120" placeholder="correo@solsersistem.net" value="${correo}">` +
        '</div>',
      showCancelButton: true,
      confirmButtonText: 'Guardar cambios',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c8102e',
      focusConfirm: false,
      preConfirm: () => {
        const a = (document.getElementById('swalAlias') as HTMLInputElement)?.value?.trim() || '';
        const c = (document.getElementById('swalCorreo') as HTMLInputElement)?.value?.trim() || '';
        if (!c) {
          swal.showValidationMessage('Ingrese un correo electrónico.');
          return false;
        }
        return { alias: a, correo: c };
      }
    }).then((result) => {
      if (!result.isConfirmed || !result.value) { return; }
      const payload = new ConfiguracionGeneral();
      payload.conf_fol_se = item.conf_fol_se;
      payload.conf_fol_se_dsc = result.value.alias;
      payload.valor1 = result.value.correo;
      this.enviarCorreo(payload);
    });
  }

  cancelarEdicion(): void {
    this.formulario = this.crearFormulario();
    this.syncView();
  }

  guardar(): void {
    if (!this.formulario.valor1?.trim()) {
      this.mostrarError('Ingrese un correo electrónico.');
      return;
    }

    const payload = new ConfiguracionGeneral();
    payload.conf_fol_se = this.formulario.conf_fol_se ?? null;
    payload.conf_fol_se_dsc = this.formulario.conf_fol_se_dsc?.trim() || '';
    payload.valor1 = this.formulario.valor1.trim();
    this.enviarCorreo(payload);
  }

  /** Alta/edición de correo copia (compartido por el formulario y el modal de edición). */
  private enviarCorreo(payload: ConfiguracionGeneral): void {
    this.guardando = true;
    this.syncView();

    this.denunciasService.guardarCorreoCopia(payload).subscribe({
      next: (resp) => {
        this.guardando = false;
        if (!resp?.codigoError) {
          this.formulario = this.crearFormulario();
          this.cargarCorreos();
          void swal.fire({
            icon: 'success',
            title: 'Configuración actualizada',
            text: 'El correo se guardó correctamente.'
          });
          return;
        }

        this.mostrarError(resp?.error || 'No se pudo guardar el correo.');
      },
      error: () => {
        this.guardando = false;
        this.mostrarError('No se pudo guardar el correo.');
      }
    });
  }

  eliminar(item: ConfiguracionGeneral): void {
    void swal.fire({
      icon: 'warning',
      title: 'Eliminar correo',
      text: `Se eliminará ${item.valor1}. ¿Deseas continuar?`,
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c8102e'
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.eliminandoId = item.conf_fol_se;
      this.syncView();

      const payload = new ConfiguracionGeneral();
      payload.conf_fol_se = item.conf_fol_se;

      this.denunciasService.eliminarCorreoCopia(payload).subscribe({
        next: (resp) => {
          this.eliminandoId = null;
          if (!resp?.codigoError) {
            this.cargarCorreos();
            if (this.formulario.conf_fol_se === item.conf_fol_se) {
              this.formulario = this.crearFormulario();
            }
            void swal.fire({
              icon: 'success',
              title: 'Correo eliminado',
              text: 'La copia administrativa fue eliminada.'
            });
            return;
          }
          this.mostrarError(resp?.error || 'No se pudo eliminar el correo.');
        },
        error: () => {
          this.eliminandoId = null;
          this.mostrarError('No se pudo eliminar el correo.');
        }
      });
    });
  }

  get editando(): boolean {
    return !!this.formulario.conf_fol_se;
  }

  // ── Paginación de "Copias configuradas" ─────────────────────────────────────
  correosPagina(): ConfiguracionGeneral[] {
    const inicio = (this.paginaNorm() - 1) * this.tamPagina;
    return this.correos.slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(): number {
    const n = this.correos.length;
    return n > 0 ? Math.ceil(n / this.tamPagina) : 1;
  }

  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  rangoInicio(): number {
    return this.correos.length === 0 ? 0 : (this.paginaNorm() - 1) * this.tamPagina + 1;
  }

  rangoFin(): number {
    return Math.min(this.paginaNorm() * this.tamPagina, this.correos.length);
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) { return; }
    this.paginaActual = p;
    this.syncView();
  }

  primeraPagina(): void { this.irPagina(1); }
  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }
  ultimaPagina(): void { this.irPagina(this.totalPaginas()); }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPaginas();
    if (total <= 1) { return [1]; }
    const actual = this.paginaNorm();
    const paginas = new Set<number>([1, total, actual - 1, actual, actual + 1]);
    const ordenadas = Array.from(paginas).filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
    const items: Array<number | string> = [];
    for (let i = 0; i < ordenadas.length; i++) {
      const p = ordenadas[i];
      const previa = ordenadas[i - 1];
      if (previa && p - previa > 1) { items.push('e' + previa); }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private crearFormulario(): ConfiguracionGeneral {
    const bean = new ConfiguracionGeneral();
    bean.conf_fol_se = null;
    bean.conf_fol_se_dsc = '';
    bean.valor1 = '';
    return bean;
  }

  private mostrarError(mensaje: string): void {
    this.guardando = false;
    this.eliminandoId = null;
    this.syncView();
    void swal.fire({
      icon: 'error',
      title: 'Operación no disponible',
      text: mensaje
    });
  }

  private syncView(): void {
    this.cdr.detectChanges();
  }
}
